import {gql} from '@apollo/client';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {addToProcessOutputRowUpdaterCache} from 'processes/tables/ProcessOutputRowUpdater';
import {addToProcessTaskRowUpdaterCache} from 'processes/tables/ProcessTaskRowUpdater';
import mergeAndSortIdObjectArrays from 'util/mergeAndSortIdObjectArrays';

import {ProcessDetailsQuery, ProcessDetailsQueryVariables} from './__generated/ProcessDetailsQuery';

type ProcessTaskGql = ExtractByTypename<ProcessDetailsQuery['process'], 'ProcessNode'>['tasks'][number];
type ProcessOutputGql = ExtractByTypename<ProcessDetailsQuery['process'], 'ProcessNode'>['outputs'][number];
type ProcessWatcher = ExtractByTypename<ProcessDetailsQuery['process'], 'ProcessNode'>['watchers'][number];

export const PROCESS_DETAILS_QUERY = gql`
  query ProcessDetailsQuery($processId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    process(id: $processId) {
      ... on ProcessNode {
        id
        name
        archiveRecord {
          purpose
        }
        permissions {
          ...ListingPermissionsGraphQL
        }
        isMappingInProgressFromProcess
        isMappingInProgressToProcess
        taskStatusCounts {
          count
          status {
            id
            displayName
            statusType
          }
        }
        processTaskSchema {
          id
          name
          prefix
          allFields
          fields
          executionType
          entryTemplate {
            id
            name
            currentPublishingRecord {
              ... on PublishingRecordNode {
                id
                publishingStatus
              }
            }
          }
          canSetAssigneeOnTaskCreation
          taskGroupInstructions
          clientOrigin
          archiveRecord {
            ...ArchiveRecordGraphQL
          }
          outputSchema {
            id
            name
            prefix
            allFields
            fields
            processExecutionType
            processTaskSchemaName
            taskFinderLookupConfig {
              id
            }
            archiveRecord {
              ...ArchiveRecordGraphQL
            }
          }
          statusLifecycle {
            ... on ProcessTaskStatusLifecycleNode {
              ...ProcessTaskStatusLifecycleGraphQL
            }
          }
        }
        watchers {
          id
          name
          handle
          avatarUrl
        }
        outputs {
          ... on ProcessOutputNode {
            ...ProcessOutputGraphQL
          }
        }
        tasks {
          ... on ProcessTaskNode {
            ...ProcessTaskGraphQL
            ...StudyableTypedValueDataGraphQL
          }
        }
        folder {
          id
        }
        rootProcess {
          ... on ProcessNode {
            id
            editUrl
            name
          }
          ...InaccessibleInfoGraphQL
        }
        hasTasksWithFailedMappings
        requestV2Submission {
          ... on RequestV2SubmissionNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ListingPermissions').fragment}
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('ProcessOutput').fragment}
  ${getFragmentWrapper('ProcessTask').fragment}
  ${getFragmentWrapper('StudyableTypedValueData').fragment}
  ${getFragmentWrapper('ArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function updateProcessDetailsQueryCache(
  processId: string,
  {
    tasks,
    mergeTasks,
    outputs,
    mergeOutputs,
    watchers,
  }: {
    tasks?: Array<ProcessTaskGql>;
    mergeTasks?: boolean;
    outputs?: Array<ProcessOutputGql>;
    mergeOutputs?: boolean;
    watchers?: Array<ProcessWatcher>;
  }
): void {
  const response = readQuerySafe<ProcessDetailsQuery, ProcessDetailsQueryVariables>(
    {query: PROCESS_DETAILS_QUERY},
    {variables: {processId}}
  );
  if (response == null) {
    return;
  }
  const process = response.process;
  assert(!isInaccessible(process), 'Cannot update inaccessible response');
  let taskStatusCounts: Array<{
    count: number;
    status: {id: string; displayName: string; statusType: ProcessTaskStatusType};
  }> | null = null;
  if (tasks != null) {
    tasks = mergeTasks ? mergeAndSortIdObjectArrays(process.tasks, tasks) : tasks;
    const unarchivedTasks = tasks.filter((task) => !isArchived(task));
    taskStatusCounts = unarchivedTasks
      .reduce(
        (acc, task) => {
          const existingStatus = acc.find((summary) => summary.status.id === task.status.id);
          if (existingStatus != null) {
            existingStatus.count += 1;
          } else {
            acc.push({count: 1, status: task.status});
          }
          return acc;
        },
        [] as Array<{
          count: number;
          status: {id: string; displayName: string; statusType: ProcessTaskStatusType};
        }>
      )
      .sort((leftStatusSummary, rightStatusSummary) =>
        leftStatusSummary.status.displayName.localeCompare(rightStatusSummary.status.displayName)
      );
  }
  if (outputs != null) {
    outputs = mergeOutputs ? mergeAndSortIdObjectArrays(process.outputs, outputs) : outputs;
  }
  const apolloClient = getApolloClient();
  apolloClient.writeQuery({
    query: PROCESS_DETAILS_QUERY,
    data: {
      process: {
        ...process,
        ...(taskStatusCounts != null ? {taskStatusCounts} : {}),
        ...(tasks != null ? {tasks} : {}),
        ...(outputs != null ? {outputs} : {}),
        ...(watchers != null ? {watchers} : {}),
      },
    },
    variables: {processId},
  });
  if (tasks != null) {
    addToProcessTaskRowUpdaterCache(tasks);
  }
  if (outputs != null) {
    addToProcessOutputRowUpdaterCache(outputs);
  }
}
